import { isProductionHost } from './environment';

export const COMING_SOON_TAG = 'Coming Soon';
export const STAGING_TAG = 'Staging';

type Tag = { name?: string | null };

function hasTag(tags: Tag[], tagName: string): boolean {
  return tags.some((tag) => tag.name === tagName);
}

/**
 * The "Coming Soon" placeholder (greyed out, not clickable) only applies on the
 * production host - off-production previewing should always be active and clickable.
 */
export function isComingSoon(tags: Tag[]): boolean {
  return isProductionHost() && hasTag(tags, COMING_SOON_TAG);
}

/**
 * Unpublished content becomes visible when previewing off the production host and
 * tagged for staging, so upcoming lessons/exercises can be reviewed before release.
 */
function isStagingPreviewAllowed(tags: Tag[]): boolean {
  return !isProductionHost() && hasTag(tags, STAGING_TAG);
}

export function isContentGroupVisible(contentGroup: { status?: string | null; tags: Tag[] }): boolean {
  return contentGroup.status === 'PUBLISHED' || isStagingPreviewAllowed(contentGroup.tags);
}

export function isContentPageVisible(page: { status?: string | null }, contentGroupTags: Tag[]): boolean {
  return page.status === 'PUBLISHED' || isStagingPreviewAllowed(contentGroupTags);
}

type ContentPageNode = {
  status?: string | null;
  contentPageChildren?: ContentPageNode[] | null;
};

/**
 * Recursively drops draft pages from a module's content-page tree unless staging
 * preview is allowed for that module's tags, mirroring isContentPageVisible at every depth.
 */
export function filterVisibleContentPages<T extends ContentPageNode>(pages: T[] | undefined | null, contentGroupTags: Tag[]): T[] {
  if (!pages) {
    return [];
  }

  return pages.filter((page) => isContentPageVisible(page, contentGroupTags)).map((page) => ({
    ...page,
    contentPageChildren: filterVisibleContentPages(page.contentPageChildren, contentGroupTags),
  }));
}
